import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { ContactForm, ValidationState } from '~concierge/types/FormTypes'
import { isValidNumber } from 'libphonenumber-js'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { useForm } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { getActiveLanguage } from 'laravel-vue-i18n'
import {
  trackFormSubmission,
  trackFormError,
  trackFormSuccess,
  trackConversion,
  trackTestDriveConversion,
  trackAdviserAppointmentConversion
} from '~conciergeUiCommons/utils/analytics'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-event-enum'

export const useSubmissionFormStore = defineStore({
  id: 'submissionForm',
  state: () => ({
    submittedSuccessfully: ref<boolean>(false),
    formData: useForm<ContactForm>({
      first_name: '',
      last_name: '',
      email: '',
      cellphone: '',
      date: '',
      time: '',
      vehicles: [],
      adviser_id: '',
      comment: '',
      language: getActiveLanguage() ?? 'fr',
      consent: false,
      captcha_token: null
    }),
    validationState: reactive<ValidationState>({
      first_name: [],
      last_name: [],
      email: [],
      cellphone: [],
      consent: [],
      comment: []
    }),
    focusState: reactive<Record<string, boolean>>({
      first_name: false,
      last_name: false,
      email: false,
      cellphone: false
    }),
    appointmentErrors: ref<Array<string>>([]),
    eventAppointmentCreated: ref<boolean>(false),
    successResponse: reactive<Record<string, string | Array<string>>>({
      type: '',
      greetings: '',
      title: '' as string,
      content: [] as Array<string>
    }),
    processing: ref<boolean>(false),
    reservationOk: ref<boolean>(false),
    hasTheAbilityToGoBackToSelection: ref<boolean>(false),
    hasCommentEnabled: ref<boolean>(true)
  }),

  getters: {
    isFormValid(state): boolean {
      return (
        Object.values(state.focusState).every(field => field === true) &&
        Object.values(state.validationState).every(field => !field.length) &&
        state.formData.consent
      )
    },
    hasFormSuccess(state): boolean {
      return state.successResponse?.type.length !== 0 && state.submittedSuccessfully
    },
    formLanguage(state): string {
      return state.formData.language
    }
  },
  actions: {
    removeCommentField(): void {
      this.hasCommentEnabled = false
      delete this.focusState.comment
      if (this.validationState.comment) {
        delete this.validationState.comment
      }
      delete this.formData.comment
    },
    setLocale(locale: string): void {
      this.formData.language = locale
    },
    resetValidationState(): void {
      this.validationState = {
        first_name: [],
        last_name: [],
        email: [],
        cellphone: [],
        consent: [],
        comment: []
      }
      if (!this.hasCommentEnabled) {
        delete this.validationState.comment
      }
      this.appointmentErrors = []
    },
    resetAppointmentData(): void {
      this.hasTheAbilityToGoBackToSelection = false
      // @ts-ignore
      this.formData.reset('date', 'time', 'vehicles', 'adviser_id')
    },

    async submitForm(submissionRoute: string): Promise<void> {
      try {
        this.hasTheAbilityToGoBackToSelection = false
        this.appointmentErrors = []
        this.buildFormData()
        const dateTime = {
          date: this.formData.date,
          time: this.formData.time
        }
        const formType = this.service === 'conciergeForm' ? 'form' : 'max'
        this.formData.post(submissionRoute, {
          preserveScroll: true,
          onStart: () => {
            console.table(dateTime)
            // Track form submission event
            const appointmentStore = useAppointmentStore()
            const appointmentType = appointmentStore.appointment.flow || 'unknown'
            trackFormSubmission(appointmentType, formType)
          },
          onSuccess: page => {
            this.submittedSuccessfully = true
            if (this.service == 'conciergeForm') {
              const success = page.props.successResponse as Record<string, string | Array<string>>
              this.successResponse = success
            }

            // Track form success and conversion events
            const appointmentStore = useAppointmentStore()
            const appointmentType = appointmentStore.appointment.flow || 'unknown'
            const appointmentId = page.props.appointmentId as string | undefined

            trackFormSuccess(appointmentType, formType, appointmentId)
            trackConversion(appointmentType, formType)

            // Track specific conversion types
            if (appointmentType === ConciergeAppointmentQuestionFlow.TestDriveAppointment) {
              const vehicles = appointmentStore.appointment.vehicles
              const vehicleInfo = vehicles && vehicles.length > 0 ? vehicles[0] : undefined
              trackTestDriveConversion(vehicleInfo, vehicles.length)
            } else if (
              appointmentType === ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment
            ) {
              trackAdviserAppointmentConversion()
            }
          },
          onError: validationErrors => {
            trackFormError(formType)

            for (const key in validationErrors) {
              if (Object.hasOwn(this.validationState, key)) {
                this.validationState[key].push(validationErrors[key])
              } else {
                this.appointmentErrors.push(validationErrors[key])
              }
              if (['date', 'time', 'adviser_id', 'vehicles'].includes(key)) {
                this.hasTheAbilityToGoBackToSelection = true
              }
            }
          }
        })
      } catch (error: unknown | Error) {
        console.error('submitForm Error submitting form:', error)
      }
    },

    async submitFormEmbed(submissionRoute: string): Promise<void> {
      try {
        this.processing = true
        this.hasTheAbilityToGoBackToSelection = false
        this.appointmentErrors = []
        this.buildFormData()

        // Track form submission event for embed form
        const appointmentStore = useAppointmentStore()
        const appointmentType = appointmentStore.appointment.flow || 'unknown'
        trackFormSubmission(appointmentType, 'direct')

        const formData = new FormData()
        const allowedKeys = [
          'first_name',
          'last_name',
          'email',
          'cellphone',
          'comment',
          'consent',
          'date',
          'time',
          'vehicles',
          'adviser_id',
          'language',
          'captcha_token'
        ] as Array<keyof ContactForm>
        Object.keys(this.formData).forEach(key => {
          if (!allowedKeys.includes(key)) {
            return
          }
          const value = this.formData[key as keyof ContactForm]
          if (key === 'vehicles' && Array.isArray(value)) {
            value.forEach((vehicle, index) => {
              formData.append(`vehicles[${index}]`, vehicle)
            })
          } else if (value !== null && value !== undefined) {
            formData.append(key, value.toString())
          }
        })

        const response = await fetch(submissionRoute, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          }
        })

        if (response.ok) {
          this.processing = false
          this.reservationOk = true

          // Track form success and conversion events for embed form
          const appointmentStore = useAppointmentStore()
          const appointmentType = appointmentStore.appointment.flow || 'unknown'

          trackFormSuccess(appointmentType)
          trackConversion(appointmentType)

          // Track specific conversion types
          if (appointmentType === ConciergeAppointmentQuestionFlow.TestDriveAppointment) {
            const vehicles = appointmentStore.appointment.vehicles
            const vehicleInfo = vehicles && vehicles.length > 0 ? vehicles[0] : undefined
            trackTestDriveConversion(vehicleInfo, vehicles.length)
          } else if (
            appointmentType === ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment
          ) {
            trackAdviserAppointmentConversion()
          }
        } else {
          const data = await response.json()
          if (data.errors) {
            for (const key in data.errors) {
              if (Object.hasOwn(this.validationState, key)) {
                this.validationState[key].push(data.errors[key][0])
              } else {
                this.appointmentErrors.push(data.errors[key][0])
              }
              if (['date', 'time', 'adviser_id', 'vehicles'].includes(key)) {
                this.hasTheAbilityToGoBackToSelection = true
              }
            }
          }
          this.processing = false
        }
      } catch (error: unknown | Error) {
        console.error('submitFormEmbed Error submitting form:', error)
        this.appointmentErrors.push('An unexpected error occurred')
        this.processing = false
      }
    },
    validate(inputName: keyof ContactForm): void {
      switch (inputName) {
        case 'email':
          this.validateEmail()
          break
        case 'cellphone':
          this.validatePhone()
          break
        case 'first_name':
          this.validateFirstName()
          break
        case 'last_name':
          this.validateLastName()
          break
        case 'comment':
          this.validateComment()
          break
        case 'consent':
          this.validateConsent()
          break
        default:
          console.error(`Validation not implemented for field: ${inputName}`)
      }
    },
    buildFormData(): void {
      const appointmentFields = useAppointmentStore().buildAppointmentForSubmission

      for (const key in appointmentFields) {
        let value = appointmentFields[key] ?? ''
        if (key === 'vehicles' && Array.isArray(value)) {
          value = value.map(vehicle => vehicle.id)
        }
        if (Object.hasOwn(appointmentFields, key)) {
          this.formData[key] = value
        }
      }
    },
    validatePhone(): void {
      this.validationState.cellphone = []
      if (!isValidNumber(this.formData.cellphone, 'CA')) {
        this.validationState.cellphone.push(trans('submission_form.form.validations.cellphone'))
      }
    },
    validateEmail(): void {
      this.validationState.email = []
      if (this.formData.email.length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.formData.email)) {
          this.validationState.email.push(trans('submission_form.form.validations.email'))
        }
      }
    },
    validateFirstName(): void {
      this.validationState.first_name = []

      if (this.formData.first_name?.length < 3) {
        this.validationState.first_name.push(trans('submission_form.form.validations.first_name'))
      }
    },
    validateLastName(): void {
      this.validationState.last_name = []

      if (this.formData.last_name?.length < 3) {
        this.validationState.last_name.push(trans('submission_form.form.validations.last_name'))
      }
    },
    validateComment(): void {
      if (!this.hasCommentEnabled) {
        return
      }
      this.validationState.comment = []

      if (this.formData.comment && this.formData.comment !== '') {
        if (this.formData.comment.length < 3) {
          this.validationState.comment.push(
            trans('submission_form.form.validations.comment.too_short')
          )
        }
        if (this.formData.comment.length > 150) {
          this.validationState.comment.push(
            trans('submission_form.form.validations.comment.too_long')
          )
        }
      }
    },
    validateConsent(): void {
      this.validationState.consent = []

      if (!this.formData.consent) {
        this.validationState.consent.push(trans('submission_form.form.validations.consent'))
      }
    }
  }
})
